<template lang="pug">
.gear-specs-wide(:class="stacked !== 'mobile' && stacked")
  .gear-specs-wide-container
    .image-container
      DynamicMedia(:config="imageConfig" :widths="[789]" :contain="true")
      .gear-spec-content(v-if="showSlot")
        slot(name="under-image")
    .table-container
      GearSpecs(:product="product" :selectedSimple="selectedSimple" :specs="specs" :dynamic="dynamic")
  slot
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'

type ProductLike = { name?: string } | Record<string, unknown>
type ImageConfig = { alt?: string } & Record<string, unknown>

const props = defineProps({
  product: {
    type    : Object as PropType<ProductLike>,
    default : () => ({}),
  },
  stacked: {
    type    : String,
    default : 'mobile',
  },
  image: {
    type     : Object as PropType<ImageConfig | undefined>,
    required : false,
  },
  showSlot: {
    type    : Boolean,
    default : false,
  },
  selectedSimple: {
    type    : Object as PropType<Record<string, unknown> | null>,
    default : null,
  },
  specs: {
    type    : Array as PropType<Array<Record<string, unknown>> | null>,
    default : null,
  },
  dynamic: {
    type    : Boolean,
    default : false,
  },
})

const imageConfig = computed<ImageConfig | undefined>(() => {
  if (!props.image) {
    return undefined
  }

  if (props.image.alt) {
    return props.image
  }

  return {
    ...props.image,
    alt: `Product Specifications: ${((props.product as ProductLike).name) ?? ''}`,
  }
})
</script>

<style lang='scss' scoped>
.gear-specs-wide {
  background-color: $color-gray-x-light;
  padding: $space-l;
}

.gear-specs-wide-container {
  margin: 0 auto;
  width: 100%;
  max-width: $max-width;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.image-container,
.table-container {
  flex-basis: 100%;
  flex-grow: 1;
}

.image-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;

  .gear-spec-content {
    width: 100%;
  }

  :deep(.media-wrap) {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

.gear-specs-wide {
  @include not-mobile() {
    .gear-specs-wide-container {
      display: flex;
    }

    .image-container {
      flex-basis: calc(66%);
      padding-right: $space-s;
    }

    .table-container {
      padding-left: $space-s;
      flex-basis: calc(34%);
    }

    &.stack-all {
      .gear-specs-wide-container {
        display: block;

        .table-container,
        .image-container {
          padding: 0;
        }

        .table-container {
          margin-top: $space-s;
        }
      }
    }

    &.stack-not-xl {
      @media (max-width: 1920px) {
        .gear-specs-wide-container {
          display: block;
        }

        .table-container,
        .image-container {
          padding: 0;
        }

        .table-container {
          margin-top: $space-s;
        }
      }
    }
  }
}</style>
